/**
 * 令牌管理工具类
 * 提供令牌的存储、验证、刷新和清理功能
 */

export interface TokenData {
  token: string
  refreshToken?: string
  expiresAt: number
  issuedAt: number
  tokenType: string
}

export class TokenManager {
  private readonly TOKEN_KEY = 'auth_token'
  private readonly TOKEN_DATA_KEY = 'auth_token_data'
  private readonly REFRESH_BUFFER_TIME = 30 * 60 * 1000 // 30分钟缓冲时间，延长刷新时间

  /**
   * 获取存储的令牌
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * 获取令牌数据
   */
  getTokenData(): TokenData | null {
    try {
      const data = localStorage.getItem(this.TOKEN_DATA_KEY)
      return data ? JSON.parse(data) : null
    } catch (error) {
      console.warn('解析令牌数据失败:', error)
      return null
    }
  }

  /**
   * 设置令牌和相关数据
   */
  setToken(token: string, expiresIn: number, refreshToken?: string): void {
    const now = Date.now()
    // 设置长期登录状态，除非手动退出否则保持登录
    // 设置为 1 年过期时间，确保用户不会因为令牌过期而被强制退出
    const longTermExpiresIn = 365 * 24 * 60 * 60 // 1年
    const extendedExpiresIn = Math.max(expiresIn, longTermExpiresIn)
    const tokenData: TokenData = {
      token,
      refreshToken,
      expiresAt: now + (extendedExpiresIn * 1000),
      issuedAt: now,
      tokenType: 'Bearer'
    }

    localStorage.setItem(this.TOKEN_KEY, token)
    localStorage.setItem(this.TOKEN_DATA_KEY, JSON.stringify(tokenData))
    
    console.log('[TokenManager] 令牌已设置，过期时间:', new Date(tokenData.expiresAt).toLocaleString())
  }

  /**
   * 移除令牌
   */
  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.TOKEN_DATA_KEY)
  }

  /**
   * 检查令牌是否过期
   */
  isTokenExpired(): boolean {
    const tokenData = this.getTokenData()
    if (!tokenData) return true

    const now = Date.now()
    return now >= tokenData.expiresAt
  }

  /**
   * 检查令牌是否即将过期（需要刷新）
   */
  shouldRefreshToken(): boolean {
    const tokenData = this.getTokenData()
    if (!tokenData) return false

    const now = Date.now()
    return now >= (tokenData.expiresAt - this.REFRESH_BUFFER_TIME)
  }

  /**
   * 检查本地令牌有效性
   * 这是一个快速的本地检查，避免不必要的网络请求
   */
  isTokenValidLocally(): boolean {
    const token = this.getToken()
    if (!token) return false

    // 检查令牌是否过期
    if (this.isTokenExpired()) {
      console.log('令牌已过期，清除本地存储')
      this.removeToken()
      return false
    }

    return true
  }

  /**
   * 获取令牌剩余有效时间（毫秒）
   */
  getTokenRemainingTime(): number {
    const tokenData = this.getTokenData()
    if (!tokenData) return 0

    const now = Date.now()
    return Math.max(0, tokenData.expiresAt - now)
  }

  /**
   * 获取令牌信息摘要（用于调试）
   */
  getTokenSummary(): {
    hasToken: boolean
    isExpired: boolean
    shouldRefresh: boolean
    remainingTime: number
    expiresAt?: string
  } {
    const hasToken = !!this.getToken()
    const isExpired = this.isTokenExpired()
    const shouldRefresh = this.shouldRefreshToken()
    const remainingTime = this.getTokenRemainingTime()
    const tokenData = this.getTokenData()

    return {
      hasToken,
      isExpired,
      shouldRefresh,
      remainingTime,
      expiresAt: tokenData ? new Date(tokenData.expiresAt).toISOString() : undefined
    }
  }
}

// 导出单例实例
export const tokenManager = new TokenManager()